import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  AdminPage, AdminHeader, AdminTitle, AdminBody,
  AdminCard, AdminCardRow, Thumbnail, CardInfo, CardMeta, StatusBadge, StarBadge,
  AdminGrid, AdminGridCard, GridThumb, GridBody, GridBadgeRow,
  ListToolbar, DisplayToggle, DisplayToggleBtn,
  FilterBar, FilterField, ClearFiltersBtn,
  Btn, BtnRow, EmptyState,
  FormSection, FormTitle, FormGrid, FormField,
  ToggleRow, ToggleSwitch,
  ImageGrid, ImageThumb, UploadZone,
  ViewerContainer, ViewerCounter, ViewerClose, ViewerNav, ViewerDelete,
  Pagination, SectionLabel,
  ChipList, Chip, InlineInputRow, HelpText, EmptyChipText,
  BackLink, ActionsSection,
  LoginWrapper, LoginForm, LoginLogo, LoginSubtitle, LoginInput, LoginError, LoginButton,
  ImgSkeleton, ImgLoaded,
} from './styles/AdminStyles';
import { supabase } from '../../services/supabaseClient';
import {
  signInAdmin, signOutAdmin, getCurrentAdmin, onAdminAuthChange,
  requestPasswordReset, updatePassword,
} from './services/adminAuthService';
import {
  formatPrice, uploadImage, uploadVideo, triggerGeocodeImovel,
  generateSeoTitle, generateSeoDescription, generateSeoKeywords,
  buildPropertySlug,
} from './services/propertyService';
import BlogAdminPanel from './admin/BlogAdminPanel';
import AdminPageV2 from './admin/v2/AdminPageV2';
import LoginGateV2 from './admin/v2/LoginGateV2';
import FormPageV2 from './admin/v2/FormPageV2';
import BlogPageV2 from './admin/v2/BlogPageV2';
import BlogFormV2 from './admin/v2/BlogFormV2';
import ConfigPageV2 from './admin/v2/ConfigPageV2';
import { MF_BASE } from './basePath';

const V2_FLAG_KEY = 'mf_admin_v2';

const VIDEO_EXTS = /\.(mp4|mov|webm|avi|mkv|m4v)(\?|$)/i;
function isVideo(url) { return VIDEO_EXTS.test(url || ''); }

// ===== Seção SEO com tabs Geral / Social =====
function SeoTab({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        flex: 1,
        padding: '10px 14px',
        background: active ? '#fff' : 'transparent',
        color: active ? '#0F1828' : '#7A8089',
        border: 'none',
        borderBottom: active ? '2px solid #0F1828' : '2px solid transparent',
        fontWeight: active ? 600 : 500,
        fontSize: 13,
        cursor: 'pointer',
        letterSpacing: 0.4,
        textTransform: 'uppercase',
      }}
    >
      {children}
    </button>
  );
}

function SeoSection({ form, set, setForm, coverImage }) {
  const [tab, setTab] = useState('geral');
  const seoTitle = form.seo_title || generateSeoTitle(form);
  const seoDesc = form.seo_description || generateSeoDescription(form);

  const handleAutoFill = () => setForm(f => ({
    ...f,
    seo_title: generateSeoTitle(f),
    seo_description: generateSeoDescription(f),
    seo_keywords: generateSeoKeywords(f),
  }));

  return (
    <FormSection>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <FormTitle style={{ margin: 0 }}>SEO</FormTitle>
        <Btn type="button" onClick={handleAutoFill}>
          Gerar automático
        </Btn>
      </div>

      {/* Tabs */}
      <div style={{
        display: 'flex',
        background: '#F4F5F7',
        borderRadius: 8,
        padding: 4,
        marginBottom: 16,
        gap: 4,
      }}>
        <SeoTab active={tab === 'geral'} onClick={() => setTab('geral')}>Geral</SeoTab>
        <SeoTab active={tab === 'social'} onClick={() => setTab('social')}>Social</SeoTab>
      </div>

      {tab === 'geral' && (
        <>
          <FormGrid>
            <FormField $full>
              <label>
                Título SEO
                <span style={{ marginLeft: 8, fontWeight: 400, fontSize: 11, color: form.seo_title.length > 70 ? '#D4380D' : '#7A8089' }}>
                  {form.seo_title.length}/70
                </span>
              </label>
              <input
                value={form.seo_title}
                onChange={set('seo_title')}
                placeholder={generateSeoTitle(form) || 'ex: Apartamento à Venda em Alphaville · 3 quartos, 112m²'}
              />
            </FormField>
            <FormField $full>
              <label>
                Meta description
                <span style={{ marginLeft: 8, fontWeight: 400, fontSize: 11, color: form.seo_description.length > 160 ? '#D4380D' : '#7A8089' }}>
                  {form.seo_description.length}/160
                </span>
              </label>
              <textarea
                rows={3}
                value={form.seo_description}
                onChange={set('seo_description')}
                placeholder={generateSeoDescription(form) || 'Descrição exibida no Google'}
                style={{
                  width: '100%', padding: '10px 12px', borderRadius: 6,
                  border: '1px solid #E7E9EC', fontFamily: 'inherit', fontSize: 14, resize: 'vertical',
                }}
              />
            </FormField>
            <FormField $full>
              <label>Palavras-chave</label>
              <input
                value={form.seo_keywords}
                onChange={set('seo_keywords')}
                placeholder={generateSeoKeywords(form) || 'apartamento alphaville, imóvel à venda...'}
              />
            </FormField>
          </FormGrid>

          {/* Preview do Google */}
          <div style={{
            marginTop: 16, padding: 14, borderRadius: 8,
            background: '#F8F9FB', border: '1px solid #E7E9EC',
            fontFamily: 'arial, sans-serif',
          }}>
            <div style={{ fontSize: 11, color: '#7A8089', marginBottom: 8, letterSpacing: 0.5, textTransform: 'uppercase' }}>
              Preview no Google
            </div>
            <div style={{ color: '#202124', fontSize: 12 }}>www.mfrealstate.com.br › imoveis</div>
            <div style={{ color: '#1A0DAB', fontSize: 18, fontWeight: 400, lineHeight: 1.3, marginTop: 2 }}>
              {(seoTitle || '—').slice(0, 70)}
            </div>
            <div style={{ color: '#4D5156', fontSize: 13, lineHeight: 1.5, marginTop: 4 }}>
              {(seoDesc || '—').slice(0, 160)}
            </div>
          </div>
        </>
      )}

      {tab === 'social' && (
        <div>
          <div style={{ fontSize: 12, color: '#7A8089', marginBottom: 16, lineHeight: 1.5 }}>
            Quando o link for compartilhado no Facebook, WhatsApp, Twitter etc.,
            esses dados aparecem. Por padrão usa o título e descrição da aba Geral.
          </div>

          {/* Preview Facebook */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 11, color: '#7A8089', marginBottom: 8, letterSpacing: 0.5, textTransform: 'uppercase' }}>
              Preview Facebook / WhatsApp
            </div>
            <div style={{
              border: '1px solid #DCDFE3',
              borderRadius: 8,
              overflow: 'hidden',
              maxWidth: 524,
              fontFamily: 'Helvetica, Arial, sans-serif',
            }}>
              <div style={{
                width: '100%',
                aspectRatio: '1.91 / 1',
                background: coverImage ? `url(${coverImage}) center/cover no-repeat` : '#E4E6EB',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#65676B', fontSize: 13,
              }}>
                {!coverImage && 'Sem imagem de capa'}
              </div>
              <div style={{ padding: '10px 12px', background: '#F0F2F5' }}>
                <div style={{ fontSize: 11, color: '#65676B', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                  mfrealstate.com.br
                </div>
                <div style={{ fontSize: 16, fontWeight: 600, color: '#050505', lineHeight: 1.2, marginTop: 4 }}>
                  {(seoTitle || '—').slice(0, 80)}
                </div>
                <div style={{ fontSize: 13, color: '#606770', lineHeight: 1.3, marginTop: 4 }}>
                  {(seoDesc || '—').slice(0, 110)}
                </div>
              </div>
            </div>
          </div>

          {/* Preview Twitter / X */}
          <div>
            <div style={{ fontSize: 11, color: '#7A8089', marginBottom: 8, letterSpacing: 0.5, textTransform: 'uppercase' }}>
              Preview Twitter / X
            </div>
            <div style={{
              border: '1px solid #CFD9DE',
              borderRadius: 14,
              overflow: 'hidden',
              maxWidth: 524,
              fontFamily: 'Helvetica, Arial, sans-serif',
            }}>
              <div style={{
                width: '100%',
                aspectRatio: '2 / 1',
                background: coverImage ? `url(${coverImage}) center/cover no-repeat` : '#E1E8ED',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#536471', fontSize: 13,
              }}>
                {!coverImage && 'Sem imagem de capa'}
              </div>
              <div style={{ padding: '10px 14px' }}>
                <div style={{ fontSize: 12, color: '#536471' }}>mfrealstate.com.br</div>
                <div style={{ fontSize: 15, color: '#0F1419', lineHeight: 1.3, marginTop: 2 }}>
                  {(seoTitle || '—').slice(0, 70)}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </FormSection>
  );
}

function LazyImg({ src, alt, style }) {
  const [loaded, setLoaded] = useState(false);

  if (isVideo(src)) {
    return (
      <div style={{ position: 'relative', width: '100%', height: '100%' }}>
        <video
          src={src}
          controls
          muted
          playsInline
          preload="metadata"
          onLoadedData={() => setLoaded(true)}
          style={{ width: '100%', height: '100%', objectFit: style?.objectFit || 'cover', display: 'block', ...style }}
        />
      </div>
    );
  }

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      {!loaded && <ImgSkeleton />}
      <ImgLoaded
        src={src}
        alt={alt || ''}
        loading="lazy"
        onLoad={() => setLoaded(true)}
        style={{
          ...style,
          opacity: loaded ? 1 : 0,
          position: loaded ? 'static' : 'absolute',
          top: 0,
          left: 0,
        }}
      />
    </div>
  );
}

function detectClassicInitialMode() {
  if (typeof window === 'undefined') return 'login';
  const params = new URLSearchParams(window.location.search);
  if (params.get('recover') === '1') return 'recover';
  if (window.location.hash.includes('type=recovery') || window.location.hash.includes('type=invite')) return 'recover';
  return 'login';
}

function LoginGate({ onAuth }) {
  const [email, setEmail] = useState('');
  const [pass, setPass] = useState('');
  const [newPass, setNewPass] = useState('');
  const [newPass2, setNewPass2] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState(detectClassicInitialMode);

  const resetMsg = () => { setError(''); setInfo(''); };

  const handleLogin = async (e) => {
    e.preventDefault();
    resetMsg(); setBusy(true);
    const res = await signInAdmin(email, pass);
    setBusy(false);
    if (!res.ok) { setError(res.error); return; }
    onAuth();
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    resetMsg();
    if (!email.trim()) { setError('Informe o email primeiro.'); return; }
    setBusy(true);
    const res = await requestPasswordReset(email);
    setBusy(false);
    if (!res.ok) { setError(res.error); return; }
    setInfo('Email de recuperação enviado.');
  };

  const handleRecover = async (e) => {
    e.preventDefault();
    resetMsg();
    if (newPass.length < 8) { setError('Senha deve ter pelo menos 8 caracteres.'); return; }
    if (newPass !== newPass2) { setError('As senhas não coincidem.'); return; }
    setBusy(true);
    const res = await updatePassword(newPass);
    setBusy(false);
    if (!res.ok) { setError(res.error); return; }
    setInfo('Senha atualizada. Faça login com a nova senha.');
    if (typeof window !== 'undefined') window.history.replaceState(null, '', window.location.pathname);
    setNewPass(''); setNewPass2(''); setPass('');
    setTimeout(() => setMode('login'), 1500);
  };

  let onSubmit = handleLogin;
  let btnLabel = 'Entrar';
  if (mode === 'forgot') { onSubmit = handleForgot; btnLabel = 'Enviar link'; }
  if (mode === 'recover') { onSubmit = handleRecover; btnLabel = 'Atualizar senha'; }

  return (
    <AdminPage>
      <LoginWrapper>
        <LoginForm onSubmit={onSubmit}>
          <LoginLogo>
            <img src="/mf-real-state/logos/mf-logo-light.png" alt="MF" />
          </LoginLogo>
          <LoginSubtitle>
            {mode === 'login' && 'Painel administrativo'}
            {mode === 'forgot' && 'Recuperar senha'}
            {mode === 'recover' && 'Definir nova senha'}
          </LoginSubtitle>

          {(mode === 'login' || mode === 'forgot') && (
            <LoginInput
              type="email"
              placeholder="Email"
              autoComplete="email"
              value={email}
              onChange={e => { setEmail(e.target.value); resetMsg(); }}
              disabled={busy}
            />
          )}
          {mode === 'login' && (
            <LoginInput
              type="password"
              placeholder="Senha"
              autoComplete="current-password"
              value={pass}
              onChange={e => { setPass(e.target.value); resetMsg(); }}
              disabled={busy}
              $last
            />
          )}
          {mode === 'recover' && (
            <>
              <LoginInput
                type="password"
                placeholder="Nova senha"
                autoComplete="new-password"
                value={newPass}
                onChange={e => { setNewPass(e.target.value); resetMsg(); }}
                disabled={busy}
              />
              <LoginInput
                type="password"
                placeholder="Confirmar nova senha"
                autoComplete="new-password"
                value={newPass2}
                onChange={e => { setNewPass2(e.target.value); resetMsg(); }}
                disabled={busy}
                $last
              />
            </>
          )}

          {error && <LoginError>{error}</LoginError>}
          {info && <LoginError style={{ color: '#3B7A3B' }}>{info}</LoginError>}

          <LoginButton type="submit" disabled={busy}>
            {busy ? 'Aguarde...' : btnLabel}
          </LoginButton>

          {mode === 'login' && (
            <button
              type="button"
              onClick={() => { resetMsg(); setMode('forgot'); }}
              style={{ background: 'transparent', border: 'none', color: '#7A8089', fontSize: 12, cursor: 'pointer', marginTop: 8, textDecoration: 'underline' }}
            >
              Esqueci minha senha
            </button>
          )}
          {mode === 'forgot' && (
            <button
              type="button"
              onClick={() => { resetMsg(); setMode('login'); }}
              style={{ background: 'transparent', border: 'none', color: '#7A8089', fontSize: 12, cursor: 'pointer', marginTop: 8, textDecoration: 'underline' }}
            >
              Voltar ao login
            </button>
          )}
        </LoginForm>
      </LoginWrapper>
    </AdminPage>
  );
}

const TABLE = 'mf_imoveis';
const EMPTY_FORM = {
  titulo: '', descricao: '', tipo: 'apartamento',
  endereco: '', numero: '', cep: '',
  bairro: 'Alphaville', cidade: 'Barueri', condominio: '',
  area_m2: '', area_construida_m2: '', quartos: '', suites: '', banheiros: '', vagas: '', andar: '',
  mobiliado: false,
  preco: '', preco_condominio: '', iptu: '',
  preco_aluguel: '', preco_condominio_aluguel: '', iptu_aluguel: '',
  seo_title: '', seo_description: '', seo_keywords: '',
  imagens: [], amenidades: [], plantas: [],
  latitude: null, longitude: null,
  bianca_product_id: '',
  incluir_no_feed: false,
  status: 'rascunho', destaque: false,
  corretor_telefone: '', corretor_nome: '',
  comissao_percentual: '', comissao_obs: '',
  corretor_id: null,
};

const DISPLAY_MODE_KEY = 'mf_admin_display_mode';

function readInitialUrlState() {
  if (typeof window === 'undefined') return { view: 'list', id: null, blogV2Edit: null };
  const p = new URLSearchParams(window.location.search);
  const v = p.get('view');
  if (v === 'form') return { view: 'form', id: p.get('id'), blogV2Edit: null };
  if (v === 'config') return { view: 'config', id: null, blogV2Edit: null };
  if (v === 'blog') {
    const blogAction = p.get('blog');
    const blogId = p.get('id');
    if (blogAction === 'new') return { view: 'blog', id: null, blogV2Edit: { action: 'new' } };
    if (blogAction === 'edit' && blogId) return { view: 'blog', id: null, blogV2Edit: { action: 'edit', id: blogId } };
    return { view: 'blog', id: null, blogV2Edit: null };
  }
  return { view: 'list', id: null, blogV2Edit: null };
}

export default function AdminPageComponent() {
  const [authState, setAuthState] = useState({ status: 'loading', admin: null });
  const authed = authState.status === 'authed';

  useEffect(() => {
    let cancelled = false;
    getCurrentAdmin().then(res => {
      if (cancelled) return;
      if (res) setAuthState({ status: 'authed', admin: res.admin });
      else setAuthState({ status: 'anon', admin: null });
    });
    const unsub = onAdminAuthChange(async (event, session) => {
      if (event === 'SIGNED_OUT' || !session) {
        setAuthState({ status: 'anon', admin: null });
        return;
      }
      // PASSWORD_RECOVERY mantém anon pra mostrar form de nova senha
      if (event === 'PASSWORD_RECOVERY') return;
      const res = await getCurrentAdmin();
      if (cancelled) return;
      if (res) setAuthState({ status: 'authed', admin: res.admin });
      else setAuthState({ status: 'anon', admin: null });
    });
    return () => { cancelled = true; unsub && unsub(); };
  }, []);
  const initialUrl = useMemo(() => readInitialUrlState(), []);
  const [view, setView] = useState(initialUrl.view); // list | form | config | blog
  const [blogClassicMode, setBlogClassicMode] = useState(false); // V2 blog cai no clássico quando precisa modal
  const [blogV2Edit, setBlogV2Edit] = useState(initialUrl.blogV2Edit); // {action: 'new'|'edit', id?: string}
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [editId, setEditId] = useState(initialUrl.id);
  const [pendingRestoreId, setPendingRestoreId] = useState(initialUrl.view === 'form' ? initialUrl.id : null);
  const [displayMode, setDisplayMode] = useState(() => {
    if (typeof window === 'undefined') return 'list';
    return localStorage.getItem(DISPLAY_MODE_KEY) === 'cards' ? 'cards' : 'list';
  });
  const [filters, setFilters] = useState({
    texto: '', status: '', tipo: '', bairro: '', cidade: '',
    precoMin: '', precoMax: '',
  });
  const setFilter = (k) => (e) => setFilters(f => ({ ...f, [k]: e.target.value }));
  const [form, setForm] = useState(EMPTY_FORM);
  const [amenidadeInput, setAmenidadeInput] = useState('');
  const [viewerIndex, setViewerIndex] = useState(null);
  const [imgPage, setImgPage] = useState(0);
  const [galleryUploading, setGalleryUploading] = useState(false);
  const [galleryProgress, setGalleryProgress] = useState({ done: 0, total: 0, stage: '', fileName: '' });
  const IMG_PER_PAGE = 24;
  const WATERMARK_LOGO_URL = '/mf-real-state/logos/logo_mf_real_white.png';

  // Debounce do rebuild: agenda dispara apos 60s sem novo save. Salva-em-lote
  // (cadastra 5 imoveis seguidos) gera 1 rebuild apenas, ~3min depois do ultimo.
  const rebuildTimerRef = useRef(null);
  const triggerRebuildDebounced = () => {
    if (rebuildTimerRef.current) clearTimeout(rebuildTimerRef.current);
    rebuildTimerRef.current = setTimeout(() => {
      fetch('/api/rebuild', { method: 'POST' })
        .then(r => {
          if (!r.ok) console.warn('[rebuild] HTTP', r.status);
          else console.log('[rebuild] disparado — Vercel rebuildando, HTML estatico atualiza em ~3min');
        })
        .catch(err => console.warn('[rebuild] erro (nao bloqueante):', err));
      rebuildTimerRef.current = null;
    }, 60_000);
  };

  // Keyboard nav for image viewer + auto-page sync
  useEffect(() => {
    if (viewerIndex === null) return;
    // Keep thumbnail page in sync with viewer
    const correctPage = Math.floor(viewerIndex / IMG_PER_PAGE);
    setImgPage(correctPage);

    const handler = (e) => {
      if (e.key === 'ArrowRight' && viewerIndex < form.imagens.length - 1) setViewerIndex(i => i + 1);
      if (e.key === 'ArrowLeft' && viewerIndex > 0) setViewerIndex(i => i - 1);
      if (e.key === 'Escape') setViewerIndex(null);
      if (e.key === 'Delete' || e.key === 'Backspace') {
        e.preventDefault();
        const next = viewerIndex >= form.imagens.length - 1 ? viewerIndex - 1 : viewerIndex;
        setForm(f => ({ ...f, imagens: f.imagens.filter((_, idx) => idx !== viewerIndex) }));
        setViewerIndex(next >= 0 ? next : null);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [viewerIndex, form.imagens.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Config state
  const [config, setConfig] = useState({ uazapi_token: '', numeros_autorizados: [], numeros_notificacao_leads: [], telefone_encaminhamento_leads: '', hero_image_url: '', hero_poster_url: '' });
  const [configLoading, setConfigLoading] = useState(false);
  const [configSaved, setConfigSaved] = useState(false);
  const [novoNumero, setNovoNumero] = useState('');
  const [novoNumeroNotif, setNovoNumeroNotif] = useState('');
  const [heroUploading, setHeroUploading] = useState(false);

  const logout = async () => {
    await signOutAdmin();
    setAuthState({ status: 'anon', admin: null });
  };

  // ===== FETCH CONFIG =====
  const fetchConfig = useCallback(async () => {
    const { data } = await supabase.from('mf_config').select('*').eq('id', 1).single();
    if (data) setConfig({
      uazapi_token: data.uazapi_token || '',
      numeros_autorizados: data.numeros_autorizados || [],
      numeros_notificacao_leads: data.numeros_notificacao_leads || [],
      telefone_encaminhamento_leads: data.telefone_encaminhamento_leads || '',
      hero_image_url: data.hero_image_url || '',
      hero_poster_url: data.hero_poster_url || '',
    });
  }, []);

  const saveConfig = async () => {
    setConfigLoading(true);
    await supabase.from('mf_config').upsert({
      id: 1,
      uazapi_token: config.uazapi_token,
      numeros_autorizados: config.numeros_autorizados,
      numeros_notificacao_leads: config.numeros_notificacao_leads,
      telefone_encaminhamento_leads: config.telefone_encaminhamento_leads || null,
      hero_image_url: config.hero_image_url || null,
      hero_poster_url: config.hero_poster_url || null,
      updated_at: new Date().toISOString(),
    });
    setConfigLoading(false);
    setConfigSaved(true);
    setTimeout(() => setConfigSaved(false), 2000);
  };

  // Extrai o primeiro frame de um File de video via canvas e retorna como Blob jpeg.
  // Retorna null se o browser nao conseguir decodificar.
  const extractVideoFirstFrame = (videoFile) => new Promise((resolve) => {
    const v = document.createElement('video');
    v.preload = 'metadata';
    v.muted = true;
    v.playsInline = true;
    v.src = URL.createObjectURL(videoFile);

    const cleanup = () => {
      URL.revokeObjectURL(v.src);
      v.remove();
    };

    v.addEventListener('loadeddata', () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = v.videoWidth;
        canvas.height = v.videoHeight;
        canvas.getContext('2d').drawImage(v, 0, 0);
        canvas.toBlob((blob) => {
          cleanup();
          resolve(blob || null);
        }, 'image/jpeg', 0.85);
      } catch (err) {
        console.warn('Falha ao extrair primeiro frame:', err);
        cleanup();
        resolve(null);
      }
    });

    v.addEventListener('error', () => {
      cleanup();
      resolve(null);
    });
  });

  // Carrega o logo da marca d'agua uma vez (cacheado entre uploads).
  const watermarkLogoRef = useRef(null);
  const loadWatermarkLogo = () => {
    if (watermarkLogoRef.current) return Promise.resolve(watermarkLogoRef.current);
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => { watermarkLogoRef.current = img; resolve(img); };
      img.onerror = reject;
      img.src = WATERMARK_LOGO_URL;
    });
  };

  // Converte HEIC/HEIF pra JPEG (browsers fora do Safari não decodificam HEIC).
  // heic2any é carregado sob demanda pra não pesar o bundle inicial.
  const convertHeicIfNeeded = async (file, onStage) => {
    const name = (file.name || '').toLowerCase();
    const isHeic = /heic|heif/.test(file.type) || name.endsWith('.heic') || name.endsWith('.heif');
    if (!isHeic) return file;
    onStage?.('Convertendo HEIC');
    try {
      const { default: heic2any } = await import('heic2any');
      const out = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.92 });
      const blob = Array.isArray(out) ? out[0] : out;
      const base = file.name.replace(/\.[^.]+$/, '');
      return new File([blob], `${base}.jpg`, { type: 'image/jpeg' });
    } catch (err) {
      console.warn('Falha ao converter HEIC:', err);
      throw new Error('Não foi possível ler este arquivo HEIC.');
    }
  };

  // Aplica marca d'agua e converte pra WebP (melhor compressão e SEO).
  // Se algo falhar, devolve o original.
  const watermarkImage = async (imageFile, onStage) => {
    try {
      onStage?.('Convertendo imagem');
      const logo = await loadWatermarkLogo();
      const bitmap = await createImageBitmap(imageFile);
      const canvas = document.createElement('canvas');
      canvas.width = bitmap.width;
      canvas.height = bitmap.height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(bitmap, 0, 0);

      // Marca d'agua centralizada e grande (~38% da menor dimensao) — dificulta remoção
      const ratio = logo.height / logo.width;
      const baseDim = Math.min(canvas.width, canvas.height);
      const targetW = baseDim * 0.38;
      const targetH = targetW * ratio;
      const x = (canvas.width - targetW) / 2;
      const y = (canvas.height - targetH) / 2;

      ctx.save();
      ctx.globalAlpha = 0.28;
      ctx.shadowColor = 'rgba(0,0,0,0.55)';
      ctx.shadowBlur = Math.max(8, canvas.width * 0.006);
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = Math.max(2, canvas.width * 0.0015);
      ctx.drawImage(logo, x, y, targetW, targetH);
      ctx.restore();

      let blob = await new Promise(res => canvas.toBlob(res, 'image/webp', 0.85));
      let ext = 'webp';
      let mime = 'image/webp';
      // Fallback caso o navegador não saiba codificar WebP (raro hoje)
      if (!blob) {
        blob = await new Promise(res => canvas.toBlob(res, 'image/jpeg', 0.9));
        ext = 'jpg';
        mime = 'image/jpeg';
      }
      if (!blob) return imageFile;
      const baseName = imageFile.name.replace(/\.[^.]+$/, '');
      return new File([blob], `${baseName}.${ext}`, { type: mime });
    } catch (err) {
      console.warn('Falha ao aplicar marca d\'agua, subindo original:', err);
      return imageFile;
    }
  };

  const handleGalleryFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;

    setGalleryUploading(true);
    setGalleryProgress({ done: 0, total: files.length, stage: '', fileName: '' });
    const folder = (form.titulo || 'imovel').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'imovel';

    for (const file of files) {
      const isVid = file.type.startsWith('video/');
      const setStage = (stage) => setGalleryProgress(p => ({ ...p, stage, fileName: file.name }));
      try {
        let url;
        if (isVid) {
          url = await uploadVideo(file, folder, setStage);
        } else {
          let toUpload = await convertHeicIfNeeded(file, setStage);
          toUpload = await watermarkImage(toUpload, setStage);
          setStage('Enviando');
          url = await uploadImage(toUpload, folder);
        }
        if (url) {
          setForm(f => ({ ...f, imagens: [...f.imagens, url] }));
        }
      } catch (err) {
        console.error('Falha no upload de', file.name, err);
        window.alert(`Erro ao processar "${file.name}": ${err.message || 'tente outro arquivo.'}`);
      }
      setGalleryProgress(p => ({ ...p, done: p.done + 1, stage: '', fileName: '' }));
    }

    setGalleryUploading(false);
    setGalleryProgress({ done: 0, total: 0, stage: '', fileName: '' });
  };

  // Re-aplica marca d'agua numa imagem ja salva. Util pra atualizar imagens
  // antigas pra logo nova. Aviso: se a imagem ja tem marca antiga, a nova fica
  // sobreposta — nao remove a antiga.
  const reapplyWatermarkAt = async (index) => {
    const url = form.imagens?.[index];
    if (!url) return;
    const VIDEO_RE = /\.(mp4|mov|webm|avi|mkv|m4v)(\?|$)/i;
    if (VIDEO_RE.test(url)) {
      window.alert('Marca d\'agua so se aplica em imagens, nao em videos.');
      return;
    }
    if (!window.confirm('Aplicar a marca d\'agua nova nesta foto?\n\nObs: se a foto ja tinha marca antiga, a nova vai ficar por cima (nao removemos a antiga).')) return;

    const originalUrl = url;
    setGalleryUploading(true);
    setGalleryProgress({ done: 0, total: 1, stage: 'Baixando original', fileName: '' });
    try {
      const res = await fetch(url, { cache: 'no-store' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      if (!blob.size) throw new Error('Imagem vazia ao baixar');
      const nameFromUrl = (url.split('/').pop() || 'foto.jpg').split('?')[0];
      const file = new File([blob], nameFromUrl, { type: blob.type || 'image/jpeg' });

      setGalleryProgress({ done: 0, total: 1, stage: 'Aplicando marca', fileName: nameFromUrl });
      const stamped = await watermarkImage(file, (stage) =>
        setGalleryProgress(p => ({ ...p, stage }))
      );

      const folder = (form.titulo || 'imovel').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'imovel';
      setGalleryProgress(p => ({ ...p, stage: 'Enviando' }));
      const newUrl = await uploadImage(stamped, folder);
      if (!newUrl) throw new Error('Falha no upload');


      setForm(f => {
        const next = [...(f.imagens || [])];
        next[index] = newUrl;
        return { ...f, imagens: next };
      });
      setGalleryProgress(p => ({ ...p, done: 1 }));
    } catch (err) {
      console.error('Falha ao reaplicar marca:', err);
      window.alert(`Erro ao aplicar marca: ${err.message || 'tente de novo.'}`);
      // Restaura URL original se algo deu errado
      setForm(f => {
        const next = [...(f.imagens || [])];
        next[index] = originalUrl;
        return { ...f, imagens: next };
      });
    } finally {
      setGalleryUploading(false);
      setTimeout(() => setGalleryProgress({ done: 0, total: 0, stage: '', fileName: '' }), 800);
    }
  };

  const handleHeroUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setHeroUploading(true);

    const isVideo = file.type.startsWith('video/');
    const url = await uploadImage(file, 'hero');

    let posterUrl = null;
    if (isVideo && url) {
      // Extrai primeiro frame e sobe como imagem separada pro bucket
      const posterBlob = await extractVideoFirstFrame(file);
      if (posterBlob) {
        const posterFile = new File([posterBlob], `poster-${Date.now()}.jpg`, { type: 'image/jpeg' });
        posterUrl = await uploadImage(posterFile, 'hero');
      }
    }

    setHeroUploading(false);
    e.target.value = '';

    if (url) {
      setConfig(c => ({
        ...c,
        hero_image_url: url,
        // Se upou imagem (nao video), limpa poster antigo. Se upou video, usa o extraido.
        hero_poster_url: isVideo ? (posterUrl || '') : '',
      }));
    } else {
      window.alert('Erro ao subir midia. Tente novamente.');
    }
  };

  const removeHeroImage = () => {
    if (!window.confirm('Remover a imagem personalizada? A capa volta pro padrao.')) return;
    setConfig(c => ({ ...c, hero_image_url: '', hero_poster_url: '' }));
  };

  const addNumero = () => {
    const num = novoNumero.replace(/\D/g, '');
    if (!num || config.numeros_autorizados.includes(num)) return;
    setConfig(c => ({ ...c, numeros_autorizados: [...c.numeros_autorizados, num] }));
    setNovoNumero('');
  };

  const removeNumero = (i) => {
    setConfig(c => ({ ...c, numeros_autorizados: c.numeros_autorizados.filter((_, idx) => idx !== i) }));
  };

  const addNumeroNotif = () => {
    const num = novoNumeroNotif.replace(/\D/g, '');
    if (!num || config.numeros_notificacao_leads.includes(num)) return;
    setConfig(c => ({ ...c, numeros_notificacao_leads: [...c.numeros_notificacao_leads, num] }));
    setNovoNumeroNotif('');
  };

  const removeNumeroNotif = (i) => {
    setConfig(c => ({ ...c, numeros_notificacao_leads: c.numeros_notificacao_leads.filter((_, idx) => idx !== i) }));
  };

  // ===== FETCH =====
  const fetchAll = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from(TABLE)
      .select('*')
      .order('created_at', { ascending: false });
    setItems(data || []);
    setLoading(false);
  }, []);

  useEffect(() => { fetchAll(); fetchConfig(); }, [fetchAll, fetchConfig]);

  // Sync URL com view+editId pra refresh nao perder estado
  useEffect(() => {
    const params = new URLSearchParams();
    if (view === 'form') {
      params.set('view', 'form');
      if (editId) params.set('id', editId);
    } else if (view === 'config') {
      params.set('view', 'config');
    } else if (view === 'blog') {
      params.set('view', 'blog');
      if (blogV2Edit?.action === 'new') {
        params.set('blog', 'new');
      } else if (blogV2Edit?.action === 'edit' && blogV2Edit.id) {
        params.set('blog', 'edit');
        params.set('id', blogV2Edit.id);
      }
    }
    const qs = params.toString();
    const newUrl = qs ? `${window.location.pathname}?${qs}` : window.location.pathname;
    if (window.location.pathname + window.location.search !== newUrl) {
      window.history.replaceState(null, '', newUrl);
    }
  }, [view, editId, blogV2Edit]);

  // Persistencia do modo de exibicao
  useEffect(() => {
    localStorage.setItem(DISPLAY_MODE_KEY, displayMode);
  }, [displayMode]);

  // ===== ACTIONS =====
  const openNew = () => {
    setEditId(null);
    setForm(EMPTY_FORM);
    setView('form');
  };

  const openEdit = (item) => {
    setEditId(item.id);
    // Split "Rua X, 123" -> rua="Rua X", numero="123"
    const enderecoFull = item.endereco || '';
    const lastComma = enderecoFull.lastIndexOf(',');
    const splitNumero = lastComma >= 0 && /^\s*\d/.test(enderecoFull.slice(lastComma + 1));
    const ruaParte = splitNumero ? enderecoFull.slice(0, lastComma).trim() : enderecoFull;
    const numeroParte = splitNumero ? enderecoFull.slice(lastComma + 1).trim() : '';
    setForm({
      ...EMPTY_FORM,
      ...item,
      endereco: ruaParte,
      numero: numeroParte,
      area_m2: item.area_m2 ?? '',
      area_construida_m2: item.area_construida_m2 ?? '',
      quartos: item.quartos ?? '',
      suites: item.suites ?? '',
      banheiros: item.banheiros ?? '',
      vagas: item.vagas ?? '',
      andar: item.andar ?? '',
      preco: item.preco ?? '',
      preco_condominio: item.preco_condominio ?? '',
      iptu: item.iptu ?? '',
      preco_aluguel: item.preco_aluguel ?? '',
      preco_condominio_aluguel: item.preco_condominio_aluguel ?? '',
      iptu_aluguel: item.iptu_aluguel ?? '',
      seo_title: item.seo_title ?? '',
      seo_description: item.seo_description ?? '',
      seo_keywords: item.seo_keywords ?? '',
      comissao_percentual: item.comissao_percentual ?? '',
      comissao_obs: item.comissao_obs ?? '',
      imagens: item.imagens || [],
      amenidades: item.amenidades || [],
    });
    setImgPage(0);
    setViewerIndex(null);
    setView('form');
  };

  // Restaura form de imovel ao abrir admin com ?view=form&id=X
  useEffect(() => {
    if (!pendingRestoreId || items.length === 0) return;
    const item = items.find(i => i.id === pendingRestoreId);
    if (item) openEdit(item);
    else setView('list'); // id na URL nao existe mais
    setPendingRestoreId(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, pendingRestoreId]);

  const handleSave = async () => {
    setSaving(true);
    // Combina rua + numero em endereco unico (DB nao tem coluna separada)
    const enderecoCompleto = form.numero
      ? `${form.endereco}, ${form.numero}`
      : form.endereco;
    const payload = {
      ...form,
      endereco: enderecoCompleto,
      area_m2: form.area_m2 ? Number(form.area_m2) : null,
      area_construida_m2: form.area_construida_m2 ? Number(form.area_construida_m2) : null,
      quartos: form.quartos ? Number(form.quartos) : null,
      suites: form.suites ? Number(form.suites) : null,
      banheiros: form.banheiros ? Number(form.banheiros) : null,
      vagas: form.vagas ? Number(form.vagas) : null,
      andar: form.andar ? Number(form.andar) : null,
      preco: form.preco ? Number(form.preco) : null,
      preco_condominio: form.preco_condominio ? Number(form.preco_condominio) : null,
      iptu: form.iptu ? Number(form.iptu) : null,
      preco_aluguel: form.preco_aluguel ? Number(form.preco_aluguel) : null,
      preco_condominio_aluguel: form.preco_condominio_aluguel ? Number(form.preco_condominio_aluguel) : null,
      iptu_aluguel: form.iptu_aluguel ? Number(form.iptu_aluguel) : null,
      comissao_percentual: form.comissao_percentual ? Number(form.comissao_percentual) : null,
      updated_at: new Date().toISOString(),
    };
    delete payload.id;
    delete payload.created_at;
    // Campos transientes (nao existem no DB)
    delete payload.numero;
    delete payload.cep;

    let savedId = editId;
    let precisaGeocode = false;

    if (editId) {
      // Re-geocodificar se endereco/bairro/cidade/condominio mudaram
      const current = items.find(i => i.id === editId);
      precisaGeocode =
        current &&
        (current.endereco !== payload.endereco ||
         current.bairro !== payload.bairro ||
         current.cidade !== payload.cidade ||
         current.condominio !== payload.condominio ||
         current.latitude == null);
      await supabase.from(TABLE).update(payload).eq('id', editId);
    } else {
      const { data: inserted } = await supabase.from(TABLE).insert(payload).select('id').single();
      savedId = inserted?.id;
      precisaGeocode = !(payload.latitude && payload.longitude);
    }

    // Fire-and-forget: dispara geocode em background, nao bloqueia UI
    if (savedId && precisaGeocode) {
      triggerGeocodeImovel(savedId).catch(err => {
        console.warn('Falha ao disparar geocode (nao bloqueante):', err);
      });
    }

    // Se foi insert, vira modo edicao do recem-criado pra continuar editando
    if (!editId && savedId) {
      setEditId(savedId);
    }

    setSaving(false);
    setSavedFlash(true);
    setTimeout(() => setSavedFlash(false), 1500);
    fetchAll();

    // Imovel publicado precisa entrar no HTML estatico do prerender pra SEO.
    // Agenda rebuild da Vercel (debounce 60s, agrupa salvar-em-lote).
    if (payload.status === 'publicado') triggerRebuildDebounced();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Excluir este imóvel?')) return;
    await supabase.from(TABLE).delete().eq('id', id);
    fetchAll();
  };

  const toggleStatus = async (item) => {
    const next = item.status === 'publicado' ? 'rascunho' : 'publicado';
    const { error } = await supabase.from(TABLE).update({ status: next }).eq('id', item.id);
    if (error) {
      alert('Erro ao atualizar status: ' + error.message);
      return;
    }
    // Atualiza o form local pra refletir mudança sem precisar dar refresh
    if (editId === item.id) setForm(f => ({ ...f, status: next }));
    fetchAll();
    // Rebuild quando status muda (qualquer direção: novo imovel publicado entra
    // no HTML estatico; despublicado precisa sair).
    triggerRebuildDebounced();
  };

  const toggleDestaque = async (item) => {
    const next = !item.destaque;
    const { error } = await supabase.from(TABLE).update({ destaque: next }).eq('id', item.id);
    if (error) {
      alert('Erro ao atualizar destaque: ' + error.message);
      return;
    }
    if (editId === item.id) setForm(f => ({ ...f, destaque: next }));
    fetchAll();
  };

  // ===== FORM HELPERS =====
  const set = (key) => (e) => setForm(f => ({ ...f, [key]: e.target.value }));
  const toggleBool = (key) => () => setForm(f => ({ ...f, [key]: !f[key] }));

  // CEP: busca endereco no ViaCEP e popula campos
  const handleCepLookup = async () => {
    const cep = (form.cep || '').replace(/\D/g, '');
    if (cep.length !== 8) return;
    try {
      const res = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
      const data = await res.json();
      if (data.erro) {
        alert('CEP não encontrado');
        return;
      }
      setForm(f => ({
        ...f,
        endereco: data.logradouro || f.endereco,
        bairro: data.bairro || f.bairro,
        cidade: data.localidade || f.cidade,
      }));
    } catch (err) {
      console.warn('Erro ViaCEP:', err);
    }
  };

  const formatCep = (v) => {
    const digits = (v || '').replace(/\D/g, '').slice(0, 8);
    if (digits.length > 5) return `${digits.slice(0, 5)}-${digits.slice(5)}`;
    return digits;
  };

  // Mascara BRL: digitos viram centavos. "100050" => 1000.50 no form, "1.000,50" exibido.
  const setMoney = (key) => (e) => {
    const digits = (e.target.value || '').replace(/\D/g, '');
    if (!digits) return setForm(f => ({ ...f, [key]: '' }));
    const n = Number(digits) / 100;
    setForm(f => ({ ...f, [key]: n }));
  };
  const fmtMoney = (v) => {
    if (v === '' || v == null) return '';
    const n = Number(v);
    if (!Number.isFinite(n)) return '';
    return n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const addAmenidade = () => {
    if (!amenidadeInput.trim()) return;
    setForm(f => ({ ...f, amenidades: [...f.amenidades, amenidadeInput.trim()] }));
    setAmenidadeInput('');
  };

  const removeAmenidade = (i) => {
    setForm(f => ({ ...f, amenidades: f.amenidades.filter((_, idx) => idx !== i) }));
  };

  const removeImage = (i) => {
    setForm(f => ({ ...f, imagens: f.imagens.filter((_, idx) => idx !== i) }));
  };

  const setAsCover = (i) => {
    if (i === 0) return;
    setForm(f => {
      const next = [...f.imagens];
      const [picked] = next.splice(i, 1);
      next.unshift(picked);
      return { ...f, imagens: next };
    });
    setViewerIndex(0);
  };

  const reorderImages = (fromIdx, toIdx) => {
    if (fromIdx === toIdx || fromIdx == null || toIdx == null) return;
    setForm(f => {
      const list = f.imagens || [];
      if (fromIdx < 0 || fromIdx >= list.length || toIdx < 0 || toIdx >= list.length) return f;
      const next = [...list];
      const [picked] = next.splice(fromIdx, 1);
      next.splice(toIdx, 0, picked);
      return { ...f, imagens: next };
    });
  };

  // ===== RENDER LIST =====
  if (authState.status === 'loading') {
    return <AdminPage style={{ minHeight: '100vh' }} />;
  }
  if (!authed) {
    const onAuth = async () => {
      const res = await getCurrentAdmin();
      setAuthState(res ? { status: 'authed', admin: res.admin } : { status: 'anon', admin: null });
    };
    const useClassicLogin = typeof window !== 'undefined' && localStorage.getItem('mf_admin_classic') === '1';
    return useClassicLogin
      ? <LoginGate onAuth={onAuth} />
      : <LoginGateV2 onAuth={onAuth} />;
  }

  // V2 é o painel padrão. Para usar o clássico, set localStorage 'mf_admin_classic' = '1'.
  const useClassic = typeof window !== 'undefined' && localStorage.getItem('mf_admin_classic') === '1';

  // V2 blog: form V2 quando editando/criando
  if (!useClassic && view === 'blog' && blogV2Edit) {
    return (
      <BlogFormV2
        postId={blogV2Edit.id || null}
        onBack={() => setBlogV2Edit(null)}
        onSaved={() => { /* mantém no form pra continuar editando */ }}
      />
    );
  }

  // V2 config
  if (!useClassic && view === 'config') {
    return (
      <ConfigPageV2
        admin={authState.admin}
        config={config}
        setConfig={setConfig}
        saveConfig={saveConfig}
        configLoading={configLoading}
        configSaved={configSaved}
        handleHeroUpload={handleHeroUpload}
        removeHeroImage={removeHeroImage}
        heroUploading={heroUploading}
        novoNumero={novoNumero}
        setNovoNumero={setNovoNumero}
        addNumero={addNumero}
        removeNumero={removeNumero}
        novoNumeroNotif={novoNumeroNotif}
        setNovoNumeroNotif={setNovoNumeroNotif}
        addNumeroNotif={addNumeroNotif}
        removeNumeroNotif={removeNumeroNotif}
        onLogout={logout}
        onNavDashboard={() => { localStorage.setItem('mf_admin_v2_nav', 'dashboard'); setView('list'); }}
        onNavImoveis={() => { localStorage.setItem('mf_admin_v2_nav', 'imoveis'); setView('list'); }}
        onNavAmenidades={() => { localStorage.setItem('mf_admin_v2_nav', 'amenidades'); setView('list'); }}
        onNavCorretores={() => { localStorage.setItem('mf_admin_v2_nav', 'corretores'); setView('list'); }}
        onNavBlog={() => setView('blog')}
        onNavDisparos={() => { localStorage.setItem('mf_admin_v2_nav', 'disparos'); setView('list'); }}
        goClassic={() => { localStorage.setItem('mf_admin_classic', '1'); window.location.reload(); }}
      />
    );
  }

  // V2 blog: lista (modais agora são nativos V2)
  if (!useClassic && view === 'blog' && !blogClassicMode) {
    return (
      <BlogPageV2
        onExit={() => setView('list')}
        onNew={() => setBlogV2Edit({ action: 'new' })}
        onEdit={(post) => setBlogV2Edit({ action: 'edit', id: post.id })}
        onLogout={logout}
        onNavDashboard={() => { localStorage.setItem('mf_admin_v2_nav', 'dashboard'); setView('list'); }}
        onNavImoveis={() => { localStorage.setItem('mf_admin_v2_nav', 'imoveis'); setView('list'); }}
        onNavAmenidades={() => { localStorage.setItem('mf_admin_v2_nav', 'amenidades'); setView('list'); }}
        onNavCorretores={() => { localStorage.setItem('mf_admin_v2_nav', 'corretores'); setView('list'); }}
        onNavVinculos={() => { localStorage.setItem('mf_admin_v2_nav', 'vinculos'); setView('list'); }}
        onNavConfig={() => setView('config')}
        goClassic={() => { localStorage.setItem('mf_admin_classic', '1'); window.location.reload(); }}
      />
    );
  }

  if (!useClassic && view === 'form') {
    return (
      <FormPageV2
        form={form}
        setForm={setForm}
        set={set}
        editId={editId}
        saving={saving}
        savedFlash={savedFlash}
        onSave={handleSave}
        onDelete={handleDelete}
        onBack={() => setView('list')}
        onToggleStatus={toggleStatus}
        onToggleDestaque={toggleDestaque}
        toggleBool={toggleBool}
        viewerIndex={viewerIndex}
        setViewerIndex={setViewerIndex}
        handleGalleryFiles={handleGalleryFiles}
        removeImage={removeImage}
        setAsCover={setAsCover}
        reorderImages={reorderImages}
        reapplyWatermarkAt={reapplyWatermarkAt}
        galleryUploading={galleryUploading}
        galleryProgress={galleryProgress}
        amenidadeInput={amenidadeInput}
        setAmenidadeInput={setAmenidadeInput}
        addAmenidade={addAmenidade}
        removeAmenidade={removeAmenidade}
        amenidadesUsadasNoutrosImoveis={items}
        handleCepLookup={handleCepLookup}
        formatCep={formatCep}
        fmtMoney={fmtMoney}
        setMoney={setMoney}
        previewHref={editId ? `${MF_BASE}/imoveis/${buildPropertySlug({ ...form, id: editId })}` : null}
        SeoSection={SeoSection}
      />
    );
  }

  if (!useClassic && view === 'list') {
    return (
      <AdminPageV2
        admin={authState.admin}
        onLogout={logout}
        onNew={openNew}
        onEdit={openEdit}
        onConfig={() => setView('config')}
        onBlog={() => setView('blog')}
        onToggleStatus={toggleStatus}
        onToggleDestaque={toggleDestaque}
        onDelete={handleDelete}
      />
    );
  }

  if (view === 'list') {
    const bairrosUnicos = Array.from(new Set(items.map(i => i.bairro).filter(Boolean))).sort();
    const cidadesUnicas = Array.from(new Set(items.map(i => i.cidade).filter(Boolean))).sort();
    const q = filters.texto.trim().toLowerCase();
    const precoMinNum = filters.precoMin ? Number(filters.precoMin) : null;
    const precoMaxNum = filters.precoMax ? Number(filters.precoMax) : null;
    const hasActiveFilter = Object.values(filters).some(v => v !== '');

    const filteredItems = items.filter(i => {
      if (q) {
        const hay = [i.titulo, i.endereco, i.bairro, i.cidade, i.condominio]
          .filter(Boolean).join(' ').toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (filters.status && i.status !== filters.status) return false;
      if (filters.tipo && i.tipo !== filters.tipo) return false;
      if (filters.bairro && i.bairro !== filters.bairro) return false;
      if (filters.cidade && i.cidade !== filters.cidade) return false;
      if (precoMinNum != null && (!i.preco || i.preco < precoMinNum)) return false;
      if (precoMaxNum != null && (!i.preco || i.preco > precoMaxNum)) return false;
      return true;
    });

    const clearFilters = () => setFilters({
      texto: '', status: '', tipo: '', bairro: '', cidade: '', precoMin: '', precoMax: '',
    });

    return (
      <AdminPage>
        <AdminHeader>
          <AdminTitle>Imóveis</AdminTitle>
          <BtnRow>
            <Btn $variant="primary" onClick={openNew}>+ Novo</Btn>
            <Btn onClick={() => setView('blog')}>Blog</Btn>
            <Btn onClick={() => setView('config')}>Config</Btn>
            <Btn
              onClick={() => { localStorage.removeItem('mf_admin_classic'); window.location.reload(); }}
              style={{ background: '#1A1F2B', color: '#fff', borderColor: '#1A1F2B' }}
              title="Voltar para o painel novo"
            >
              Voltar para o novo
            </Btn>
            <Btn $variant="ghost" onClick={logout}>Sair</Btn>
          </BtnRow>
        </AdminHeader>
        <AdminBody $wide={displayMode === 'cards'}>
          {loading ? (
            <EmptyState>Carregando...</EmptyState>
          ) : items.length === 0 ? (
            <EmptyState>Nenhum imóvel cadastrado.</EmptyState>
          ) : (
            <>
              <ListToolbar>
                <DisplayToggle role="tablist" aria-label="Modo de exibição">
                  <DisplayToggleBtn
                    type="button"
                    $active={displayMode === 'list'}
                    onClick={() => setDisplayMode('list')}
                    aria-pressed={displayMode === 'list'}
                  >Lista</DisplayToggleBtn>
                  <DisplayToggleBtn
                    type="button"
                    $active={displayMode === 'cards'}
                    onClick={() => setDisplayMode('cards')}
                    aria-pressed={displayMode === 'cards'}
                  >Cards</DisplayToggleBtn>
                </DisplayToggle>
              </ListToolbar>
              <FilterBar>
                <FilterField>
                  <span>Buscar</span>
                  <input
                    type="search"
                    placeholder="Título, endereço, condomínio…"
                    value={filters.texto}
                    onChange={setFilter('texto')}
                  />
                </FilterField>
                <FilterField>
                  <span>Status</span>
                  <select value={filters.status} onChange={setFilter('status')}>
                    <option value="">Todos</option>
                    <option value="publicado">Publicado</option>
                    <option value="rascunho">Rascunho</option>
                    <option value="vendido">Vendido</option>
                    <option value="inativo">Inativo</option>
                  </select>
                </FilterField>
                <FilterField>
                  <span>Tipo</span>
                  <select value={filters.tipo} onChange={setFilter('tipo')}>
                    <option value="">Todos</option>
                    <option value="apartamento">Apartamento</option>
                    <option value="casa">Casa</option>
                    <option value="studio">Studio</option>
                    <option value="terreno">Terreno</option>
                    <option value="comercial">Comercial</option>
                  </select>
                </FilterField>
                <FilterField>
                  <span>Bairro</span>
                  <select value={filters.bairro} onChange={setFilter('bairro')}>
                    <option value="">Todos</option>
                    {bairrosUnicos.map(b => <option key={b} value={b}>{b}</option>)}
                  </select>
                </FilterField>
                <FilterField>
                  <span>Cidade</span>
                  <select value={filters.cidade} onChange={setFilter('cidade')}>
                    <option value="">Todas</option>
                    {cidadesUnicas.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </FilterField>
                <FilterField>
                  <span>Preço mín</span>
                  <input
                    type="number"
                    placeholder="R$"
                    value={filters.precoMin}
                    onChange={setFilter('precoMin')}
                  />
                </FilterField>
                <FilterField>
                  <span>Preço máx</span>
                  <input
                    type="number"
                    placeholder="R$"
                    value={filters.precoMax}
                    onChange={setFilter('precoMax')}
                  />
                </FilterField>
                <ClearFiltersBtn
                  type="button"
                  onClick={clearFilters}
                  disabled={!hasActiveFilter}
                  title="Limpar todos os filtros"
                >Limpar</ClearFiltersBtn>
              </FilterBar>
              <SectionLabel>
                {filteredItems.length} {filteredItems.length === 1 ? 'imóvel' : 'imóveis'}
                {hasActiveFilter && filteredItems.length !== items.length ? ` (de ${items.length})` : ''}
              </SectionLabel>
              {filteredItems.length === 0 ? (
                <EmptyState>Nenhum imóvel encontrado com esses filtros.</EmptyState>
              ) : displayMode === 'cards' ? (
                <AdminGrid>
                  {filteredItems.map(item => (
                    <AdminGridCard key={item.id} onClick={() => openEdit(item)}>
                      <GridThumb>
                        {item.imagens?.[0] && <LazyImg src={item.imagens[0]} />}
                      </GridThumb>
                      <GridBody>
                        <h3>{item.titulo}</h3>
                        <p>
                          {[item.bairro, item.cidade].filter(Boolean).join(', ')}
                        </p>
                        <p>
                          {item.preco ? formatPrice(item.preco) : 'Sob consulta'}
                          {item.area_m2 ? ` · ${item.area_m2}m²` : ''}
                        </p>
                        <GridBadgeRow>
                          <StatusBadge $status={item.status}>{item.status}</StatusBadge>
                          {item.destaque && <StarBadge>★</StarBadge>}
                        </GridBadgeRow>
                      </GridBody>
                    </AdminGridCard>
                  ))}
                </AdminGrid>
              ) : (
                <AdminCard>
                  {filteredItems.map(item => (
                    <AdminCardRow key={item.id} onClick={() => openEdit(item)}>
                      <Thumbnail>
                        {item.imagens?.[0] && <LazyImg src={item.imagens[0]} />}
                      </Thumbnail>
                      <CardInfo>
                        <h3>{item.titulo}</h3>
                        <p>
                          {item.bairro}
                          {item.preco ? ` · ${formatPrice(item.preco)}` : ''}
                          {item.area_m2 ? ` · ${item.area_m2}m²` : ''}
                        </p>
                      </CardInfo>
                      <CardMeta>
                        {item.destaque && <StarBadge>★</StarBadge>}
                        <StatusBadge $status={item.status}>{item.status}</StatusBadge>
                      </CardMeta>
                    </AdminCardRow>
                  ))}
                </AdminCard>
              )}
            </>
          )}
        </AdminBody>
      </AdminPage>
    );
  }

  // ===== RENDER CONFIG =====
  if (view === 'config') {
    return (
      <AdminPage>
        <AdminHeader>
          <BtnRow>
            <BackLink onClick={() => setView('list')}>← Voltar</BackLink>
            <AdminTitle>Configurações</AdminTitle>
          </BtnRow>
          <Btn $variant="primary" onClick={saveConfig} disabled={configLoading}>
            {configSaved ? 'Salvo!' : configLoading ? 'Salvando...' : 'Salvar'}
          </Btn>
        </AdminHeader>
        <AdminBody>
          <FormSection>
            <FormTitle>Capa da home</FormTitle>
            <HelpText>
              Imagem de fundo do topo da página inicial. Se nenhuma for definida, usa a padrão.
            </HelpText>
            <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
              <div style={{
                width: 320,
                height: 180,
                borderRadius: 8,
                overflow: 'hidden',
                border: '1px solid #E5E5EA',
                background: '#F2F2F7',
                position: 'relative',
              }}>
                {config.hero_image_url ? (
                  isVideo(config.hero_image_url) ? (
                    <video
                      src={config.hero_image_url}
                      autoPlay
                      muted
                      loop
                      playsInline
                      style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                    />
                  ) : (
                    <img
                      src={config.hero_image_url}
                      alt="Capa atual"
                      style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                    />
                  )
                ) : (
                  <div style={{
                    width: '100%', height: '100%',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: '#8E8E93', fontSize: 13,
                  }}>
                    Usando imagem padrão
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <label>
                  <input
                    type="file"
                    accept="image/*,video/mp4,video/webm,video/quicktime"
                    onChange={handleHeroUpload}
                    disabled={heroUploading}
                    style={{ display: 'none' }}
                  />
                  <Btn as="span" $variant="primary" style={{ cursor: heroUploading ? 'wait' : 'pointer' }}>
                    {heroUploading ? 'Enviando...' : config.hero_image_url ? 'Trocar mídia' : 'Enviar imagem ou vídeo'}
                  </Btn>
                </label>
                {config.hero_image_url && (
                  <Btn $variant="ghost" onClick={removeHeroImage} disabled={heroUploading}>
                    Remover
                  </Btn>
                )}
                <HelpText style={{ marginTop: 4 }}>
                  Aceita imagem ou vídeo (mp4/webm). Vídeos devem ter menos de 10MB — otimize antes de subir.
                  Não esqueça de clicar em <b>Salvar</b> no topo após trocar.
                </HelpText>
              </div>
            </div>
          </FormSection>

          <FormSection>
            <FormTitle>WhatsApp (uazapi)</FormTitle>
            <FormGrid>
              <FormField $full>
                <label>Token da instância uazapi</label>
                <input
                  value={config.uazapi_token}
                  onChange={e => setConfig(c => ({ ...c, uazapi_token: e.target.value }))}
                  placeholder="Ex: 1a181e60-97d5-4361-9626-..."
                  style={{ fontFamily: 'monospace', fontSize: 13 }}
                />
              </FormField>
            </FormGrid>
          </FormSection>

          <FormSection>
            <FormTitle>Números autorizados ({config.numeros_autorizados.length})</FormTitle>
            <HelpText>
              Apenas esses números podem cadastrar imóveis pelo WhatsApp.
              Formato: código do país + DDD + número (ex: 5511999999999)
            </HelpText>
            <ChipList $mb="16px">
              {config.numeros_autorizados.map((num, i) => (
                <Chip key={i} $mono>
                  {num}
                  <button onClick={() => removeNumero(i)}>×</button>
                </Chip>
              ))}
              {config.numeros_autorizados.length === 0 && (
                <EmptyChipText>Nenhum número adicionado</EmptyChipText>
              )}
            </ChipList>
            <InlineInputRow $mono>
              <input
                value={novoNumero}
                onChange={e => setNovoNumero(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addNumero())}
                placeholder="5511999999999"
              />
              <Btn onClick={addNumero}>Adicionar</Btn>
            </InlineInputRow>
          </FormSection>

          <FormSection>
            <FormTitle>Notificação de leads do site ({config.numeros_notificacao_leads.length})</FormTitle>
            <HelpText>
              Esses números recebem uma mensagem no WhatsApp quando alguém preenche
              o formulário de contato no site. Formato: 5511999999999
            </HelpText>
            <ChipList $mb="16px">
              {config.numeros_notificacao_leads.map((num, i) => (
                <Chip key={i} $mono>
                  {num}
                  <button onClick={() => removeNumeroNotif(i)}>×</button>
                </Chip>
              ))}
              {config.numeros_notificacao_leads.length === 0 && (
                <EmptyChipText>Nenhum número adicionado</EmptyChipText>
              )}
            </ChipList>
            <InlineInputRow $mono>
              <input
                value={novoNumeroNotif}
                onChange={e => setNovoNumeroNotif(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addNumeroNotif())}
                placeholder="5511999999999"
              />
              <Btn onClick={addNumeroNotif}>Adicionar</Btn>
            </InlineInputRow>
          </FormSection>

          <FormSection>
            <FormTitle>Encaminhamento de leads</FormTitle>
            <HelpText>
              Telefone único que recebe os contatos dos botões "Agendar visita" e
              "Quero mais informações" nas páginas dos imóveis. Formato: 5511999999999
            </HelpText>
            <InlineInputRow $mono>
              <input
                value={config.telefone_encaminhamento_leads}
                onChange={e => setConfig(c => ({ ...c, telefone_encaminhamento_leads: e.target.value.replace(/\D/g, '') }))}
                placeholder="5511999999999"
              />
            </InlineInputRow>
          </FormSection>
        </AdminBody>
      </AdminPage>
    );
  }

  // ===== RENDER BLOG =====
  if (view === 'blog') {
    const handleBlogExit = () => {
      if (blogClassicMode) {
        // estava em modo temporário a partir do V2 → volta pro V2
        setBlogClassicMode(false);
      } else {
        // veio do clássico, volta pra list clássica
        setView('list');
      }
    };
    return (
      <AdminPage>
        <BlogAdminPanel onExit={handleBlogExit} />
      </AdminPage>
    );
  }

  // ===== RENDER FORM =====
  return (
    <AdminPage>
      <AdminHeader>
        <BtnRow>
          <BackLink onClick={() => setView('list')}>← Voltar</BackLink>
          <AdminTitle>{editId ? 'Editar' : 'Novo imóvel'}</AdminTitle>
        </BtnRow>
        <BtnRow>
          {editId && (
            <>
              <Btn
                $variant="ghost"
                as="a"
                href={`${MF_BASE}/imoveis/${buildPropertySlug({ ...form, id: editId })}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                Preview
              </Btn>
              <Btn
                $variant={form.status === 'publicado' ? 'warning' : 'success'}
                onClick={() => toggleStatus({ id: editId, status: form.status })}
              >
                {form.status === 'publicado' ? 'Despublicar' : 'Publicar'}
              </Btn>
              <Btn
                $variant={form.destaque ? 'ghost' : 'star'}
                onClick={() => toggleDestaque({ id: editId, destaque: form.destaque })}
              >
                {form.destaque ? 'Remover destaque' : 'Marcar destaque'}
              </Btn>
              <Btn $variant="danger" onClick={() => { handleDelete(editId); setView('list'); }}>
                Excluir
              </Btn>
            </>
          )}
          <Btn $variant="primary" onClick={handleSave} disabled={saving || !form.titulo}>
            {saving ? 'Salvando...' : savedFlash ? 'Salvo!' : 'Salvar'}
          </Btn>
        </BtnRow>
      </AdminHeader>

      <AdminBody>
        {/* Basico */}
        <FormSection>
          <FormTitle>Informações básicas</FormTitle>
          <FormGrid>
            <FormField $full>
              <label>Título</label>
              <input value={form.titulo} onChange={set('titulo')} placeholder="Ex: Apartamento Gran Alphaville" />
            </FormField>
            <FormField>
              <label>Tipo</label>
              <select value={form.tipo} onChange={set('tipo')}>
                <option value="apartamento">Apartamento</option>
                <option value="casa">Casa</option>
                <option value="studio">Studio</option>
                <option value="terreno">Terreno</option>
                <option value="comercial">Comercial</option>
              </select>
            </FormField>
            <FormField>
              <label>Status</label>
              <select value={form.status} onChange={set('status')}>
                <option value="rascunho">Rascunho</option>
                <option value="publicado">Publicado</option>
                <option value="vendido">Vendido</option>
                <option value="inativo">Inativo</option>
              </select>
            </FormField>
            <FormField>
              <ToggleRow>
                <span>Destaque na home</span>
                <ToggleSwitch $on={form.destaque} onClick={toggleBool('destaque')} type="button" />
              </ToggleRow>
            </FormField>
            <FormField $full>
              <label>Descrição</label>
              <textarea value={form.descricao} onChange={set('descricao')} rows={4} />
            </FormField>
          </FormGrid>
        </FormSection>

        {/* Localizacao */}
        <FormSection>
          <FormTitle>Localização</FormTitle>
          <FormGrid>
            <FormField>
              <label>CEP</label>
              <input
                value={formatCep(form.cep)}
                onChange={(e) => setForm(f => ({ ...f, cep: formatCep(e.target.value) }))}
                onBlur={handleCepLookup}
                placeholder="00000-000"
                maxLength={9}
              />
            </FormField>
            <FormField>
              <label>Endereço (rua)</label>
              <input value={form.endereco} onChange={set('endereco')} placeholder="Alameda Manhattan" />
            </FormField>
            <FormField>
              <label>Número</label>
              <input value={form.numero} onChange={set('numero')} placeholder="114" />
            </FormField>
            <FormField>
              <label>Bairro</label>
              <input
                value={form.bairro}
                onChange={set('bairro')}
                list="bairros-sugestoes"
                placeholder="Digite ou selecione"
              />
              <datalist id="bairros-sugestoes">
                <option value="Alphaville" />
                <option value="Tamboré" />
                <option value="Barueri Centro" />
              </datalist>
            </FormField>
            <FormField>
              <label>Cidade</label>
              <input value={form.cidade} onChange={set('cidade')} />
            </FormField>
            <FormField>
              <label>Condomínio</label>
              <input value={form.condominio} onChange={set('condominio')} />
            </FormField>
          </FormGrid>
        </FormSection>

        {/* Specs */}
        <FormSection>
          <FormTitle>Especificações</FormTitle>
          <FormGrid>
            <FormField>
              <label>Área Terreno (m²)</label>
              <input type="number" value={form.area_m2} onChange={set('area_m2')} />
            </FormField>
            <FormField>
              <label>Área Construída (m²)</label>
              <input type="number" value={form.area_construida_m2} onChange={set('area_construida_m2')} />
            </FormField>
            <FormField>
              <label>Quartos</label>
              <input type="number" value={form.quartos} onChange={set('quartos')} />
            </FormField>
            <FormField>
              <label>Suítes</label>
              <input type="number" value={form.suites} onChange={set('suites')} />
            </FormField>
            <FormField>
              <label>Banheiros</label>
              <input type="number" value={form.banheiros} onChange={set('banheiros')} />
            </FormField>
            <FormField>
              <label>Vagas</label>
              <input type="number" value={form.vagas} onChange={set('vagas')} />
            </FormField>
            <FormField>
              <label>Andar</label>
              <input type="number" value={form.andar} onChange={set('andar')} />
            </FormField>
            <FormField>
              <ToggleRow>
                <span>Mobiliado</span>
                <ToggleSwitch $on={form.mobiliado} onClick={toggleBool('mobiliado')} type="button" />
              </ToggleRow>
            </FormField>
          </FormGrid>
        </FormSection>

        {/* Financeiro */}
        <FormSection>
          <FormTitle>Financeiro</FormTitle>

          <div style={{ fontSize: 12, fontWeight: 600, opacity: 0.7, marginBottom: 8, letterSpacing: 0.5 }}>VENDA</div>
          <FormGrid>
            <FormField>
              <label>Preço (R$)</label>
              <input type="text" inputMode="numeric" value={fmtMoney(form.preco)} onChange={setMoney('preco')} placeholder="0,00" />
            </FormField>
            <FormField>
              <label>Condomínio (R$/mês)</label>
              <input type="text" inputMode="numeric" value={fmtMoney(form.preco_condominio)} onChange={setMoney('preco_condominio')} placeholder="0,00" />
            </FormField>
            <FormField>
              <label>IPTU (R$/mês)</label>
              <input type="text" inputMode="numeric" value={fmtMoney(form.iptu)} onChange={setMoney('iptu')} placeholder="0,00" />
            </FormField>
          </FormGrid>

          <div style={{ fontSize: 12, fontWeight: 600, opacity: 0.7, margin: '20px 0 8px', letterSpacing: 0.5 }}>ALUGUEL</div>
          <FormGrid>
            <FormField>
              <label>Preço (R$/mês)</label>
              <input type="text" inputMode="numeric" value={fmtMoney(form.preco_aluguel)} onChange={setMoney('preco_aluguel')} placeholder="0,00" />
            </FormField>
            <FormField>
              <label>Condomínio (R$/mês)</label>
              <input type="text" inputMode="numeric" value={fmtMoney(form.preco_condominio_aluguel)} onChange={setMoney('preco_condominio_aluguel')} placeholder="0,00" />
            </FormField>
            <FormField>
              <label>IPTU (R$/mês)</label>
              <input type="text" inputMode="numeric" value={fmtMoney(form.iptu_aluguel)} onChange={setMoney('iptu_aluguel')} placeholder="0,00" />
            </FormField>
          </FormGrid>
        </FormSection>

        {/* SEO */}
        <SeoSection
          form={form}
          set={set}
          setForm={setForm}
          property={form}
          editId={editId}
          coverImage={(form.imagens || []).find(u => !isVideo(u)) || ''}
        />

        {/* Corretor */}
        <FormSection>
          <FormTitle>Corretor</FormTitle>
          <FormGrid>
            <FormField>
              <label>Nome</label>
              <input value={form.corretor_nome} onChange={set('corretor_nome')} />
            </FormField>
            <FormField>
              <label>Telefone (com 55)</label>
              <input value={form.corretor_telefone} onChange={set('corretor_telefone')} placeholder="5511999999999" />
            </FormField>
            <FormField>
              <label>Comissão (%)</label>
              <input type="number" value={form.comissao_percentual} onChange={set('comissao_percentual')} />
            </FormField>
            <FormField $full>
              <label>Obs comissão</label>
              <input value={form.comissao_obs} onChange={set('comissao_obs')} />
            </FormField>
          </FormGrid>
        </FormSection>

        {/* Mídia — Viewer compartilhado entre Fotos e Vídeos */}
        {viewerIndex !== null && form.imagens[viewerIndex] && (
          <FormSection>
            <ViewerContainer>
              {isVideo(form.imagens[viewerIndex]) ? (
                <video
                  src={form.imagens[viewerIndex]}
                  controls
                  autoPlay
                  style={{ width: '100%', maxHeight: 400, objectFit: 'contain', background: '#000' }}
                />
              ) : (
                <LazyImg
                  src={form.imagens[viewerIndex]}
                  alt={`Mídia ${viewerIndex + 1}`}
                  style={{ width: '100%', maxHeight: 400, objectFit: 'contain' }}
                />
              )}
              <ViewerCounter>{viewerIndex + 1} / {form.imagens.length}</ViewerCounter>
              <ViewerClose onClick={() => setViewerIndex(null)}>×</ViewerClose>
              {viewerIndex > 0 && (
                <ViewerNav $dir="prev" onClick={() => setViewerIndex(i => i - 1)}>‹</ViewerNav>
              )}
              {viewerIndex < form.imagens.length - 1 && (
                <ViewerNav $dir="next" onClick={() => setViewerIndex(i => i + 1)}>›</ViewerNav>
              )}
              <ViewerDelete style={{ display: 'flex', gap: 8 }}>
                {viewerIndex !== 0 && !isVideo(form.imagens[viewerIndex]) && (
                  <button
                    type="button"
                    onClick={() => setAsCover(viewerIndex)}
                    style={{ background: 'rgba(0,0,0,0.65)' }}
                  >
                    ★ Definir como capa
                  </button>
                )}
                {viewerIndex === 0 && (
                  <span
                    style={{
                      background: 'rgba(255,204,0,0.85)',
                      color: '#000',
                      borderRadius: 8,
                      padding: '7px 18px',
                      fontSize: 14,
                      fontWeight: 600,
                    }}
                  >
                    ★ Capa atual
                  </span>
                )}
                <button
                  onClick={() => {
                    const next = viewerIndex >= form.imagens.length - 1 ? viewerIndex - 1 : viewerIndex;
                    removeImage(viewerIndex);
                    setViewerIndex(next >= 0 ? next : null);
                  }}
                >
                  Excluir
                </button>
              </ViewerDelete>
            </ViewerContainer>
          </FormSection>
        )}

        {/* FOTOS */}
        {(() => {
          const photoIndices = form.imagens
            .map((u, i) => ({ u, i }))
            .filter(({ u }) => !isVideo(u));
          return (
            <FormSection>
              <FormTitle>Fotos ({photoIndices.length})</FormTitle>
              <ImageGrid>
                {photoIndices.map(({ u: url, i }) => (
                  <ImageThumb
                    key={i}
                    $active={viewerIndex === i}
                    onClick={() => setViewerIndex(i)}
                  >
                    <LazyImg src={url} alt={`Foto ${i + 1}`} />
                    {i === 0 && (
                      <span
                        style={{
                          position: 'absolute', top: 3, left: 3,
                          background: 'rgba(255,204,0,0.9)', color: '#000',
                          borderRadius: 4, padding: '1px 5px',
                          fontSize: 9, fontWeight: 700, lineHeight: 1.2,
                          pointerEvents: 'none',
                        }}
                      >
                        ★ CAPA
                      </span>
                    )}
                    <button onClick={(e) => { e.stopPropagation(); removeImage(i); }}>×</button>
                  </ImageThumb>
                ))}
                <UploadZone>
                  {galleryUploading
                    ? `${galleryProgress.done}/${galleryProgress.total}`
                    : '+ Foto'}
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    style={{ display: 'none' }}
                    disabled={galleryUploading}
                    onChange={handleGalleryFiles}
                  />
                </UploadZone>
              </ImageGrid>
              <HelpText style={{ marginTop: 8 }}>
                Fotos recebem marca d'água automática centralizada.
              </HelpText>
            </FormSection>
          );
        })()}

        {/* VÍDEOS */}
        {(() => {
          const videoIndices = form.imagens
            .map((u, i) => ({ u, i }))
            .filter(({ u }) => isVideo(u));
          return (
            <FormSection>
              <FormTitle>Vídeos ({videoIndices.length})</FormTitle>
              <ImageGrid>
                {videoIndices.map(({ u: url, i }) => (
                  <ImageThumb
                    key={i}
                    $active={viewerIndex === i}
                    onClick={() => setViewerIndex(i)}
                  >
                    <video
                      src={`${url}#t=0.1`}
                      muted
                      playsInline
                      preload="metadata"
                      tabIndex={-1}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                    <span
                      style={{
                        position: 'absolute', top: '50%', left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: 32, height: 32, borderRadius: '50%',
                        background: 'rgba(15, 24, 40, 0.7)', color: '#fff',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        pointerEvents: 'none',
                      }}
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" style={{ marginLeft: 2 }}>
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    </span>
                    <button onClick={(e) => { e.stopPropagation(); removeImage(i); }}>×</button>
                  </ImageThumb>
                ))}
                <UploadZone>
                  {galleryUploading
                    ? `${galleryProgress.done}/${galleryProgress.total}`
                    : '+ Vídeo'}
                  <input
                    type="file"
                    accept="video/mp4,video/webm,video/quicktime,video/x-matroska"
                    multiple
                    style={{ display: 'none' }}
                    disabled={galleryUploading}
                    onChange={handleGalleryFiles}
                  />
                </UploadZone>
              </ImageGrid>
              <HelpText style={{ marginTop: 8 }}>
                Vídeos são enviados sem marca d'água. Formatos aceitos: mp4, webm, mov, mkv.
              </HelpText>
            </FormSection>
          );
        })()}

        {/* Amenidades */}
        <FormSection>
          <FormTitle>Amenidades ({form.amenidades.length})</FormTitle>
          <ChipList $gap="6px" $mb="12px">
            {form.amenidades.map((a, i) => (
              <Chip key={i} $compact>
                {a}
                <button onClick={() => removeAmenidade(i)}>×</button>
              </Chip>
            ))}
          </ChipList>
          <InlineInputRow>
            <input
              value={amenidadeInput}
              onChange={e => setAmenidadeInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addAmenidade())}
              placeholder="Adicionar amenidade..."
            />
            <Btn onClick={addAmenidade}>+</Btn>
          </InlineInputRow>
        </FormSection>

      </AdminBody>
    </AdminPage>
  );
}
